/*
 * GA4 ecommerce dataLayer — click and cart events.
 * Loaded in layout/theme.liquid after the GTM container snippet.
 *
 * select_item      — delegated click on any [data-dl-item] product card.
 * add_to_cart      — cart AJAX endpoints are intercepted and the cart diffed,
 * remove_from_cart   so quick-add, drawer, steppers and apps are all covered
 *                    without binding to buttons.
 */
(function () {
  /* ---------------------------------------------------------------- select_item */

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-dl-item]');
    if (!card) return;

    // Some cards carry an add-to-cart control (the drawer upsell). Pressing it
    // is not selecting the item — that click belongs to add_to_cart.
    if (e.target.closest('button, form')) return;

    var item;
    try {
      item = JSON.parse(card.getAttribute('data-dl-item'));
    } catch (err) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        item_list_id: card.getAttribute('data-dl-list-id') || undefined,
        item_list_name: card.getAttribute('data-dl-list-name') || undefined,
        items: [item]
      }
    });
  });

  /* ------------------------------------------- add_to_cart / remove_from_cart */

  var snapshot = {};
  var lastCart = null;

  function push(eventName, items, currency) {
    if (!items.length) return;

    var value = items.reduce(function (sum, i) {
      return sum + i.price * i.quantity;
    }, 0);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        currency: currency,
        value: Math.round(value * 100) / 100,
        items: items
      }
    });
  }

  function mapLine(line, quantity) {
    return {
      item_id: line.sku || String(line.variant_id),
      item_name: line.product_title,
      item_brand: line.vendor,
      item_category: line.product_type,
      item_variant: line.variant_title || undefined,
      price: line.final_price / 100,
      quantity: quantity != null ? quantity : line.quantity
    };
  }

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  function takeSnapshot(cart) {
    snapshot = {};
    lastCart = cart;
    if (!cart || !cart.items) return;
    cart.items.forEach(function (line) {
      snapshot[line.key] = { quantity: line.quantity, line: line };
    });
  }

  function diff() {
    var before = snapshot;

    return readCart().then(function (cart) {
      if (!cart) return;

      var added = [];
      var removed = [];
      var after = {};

      (cart.items || []).forEach(function (line) {
        after[line.key] = line;
        var prevQty = before[line.key] ? before[line.key].quantity : 0;
        var delta = line.quantity - prevQty;
        if (delta > 0) added.push(mapLine(line, delta));
        if (delta < 0) removed.push(mapLine(line, Math.abs(delta)));
      });

      Object.keys(before).forEach(function (key) {
        if (!after[key]) removed.push(mapLine(before[key].line, before[key].quantity));
      });

      // cart.js always reports the active currency, so it never has to be
      // hardcoded or guessed from a global that may not exist.
      var currency =
        cart.currency ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        undefined;

      push('add_to_cart', added, currency);
      push('remove_from_cart', removed, currency);
      takeSnapshot(cart);
    });
  }

  // Cart mutations can overlap — a shopper holding down a quantity stepper
  // fires several. Serialising means each diff sees the previous one's
  // snapshot, so deltas stay correct instead of being double-counted.
  var queue = Promise.resolve();
  function diffAndPush() {
    queue = queue.then(diff).catch(function () {});
    return queue;
  }

  var CART_ENDPOINTS = /\/cart\/(add|change|update|clear)(\.js)?/;

  // fetch — used by this theme's add-to-cart and cart drawer
  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = args[0] instanceof Request ? args[0].url : String(args[0]);
    return originalFetch.apply(this, args).then(function (response) {
      if (CART_ENDPOINTS.test(url) && response.ok) diffAndPush();
      return response;
    });
  };

  // XHR — third-party apps and anything jQuery-based
  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dlUrl = url;
    return originalOpen.apply(this, arguments);
  };

  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    xhr.addEventListener('load', function () {
      if (CART_ENDPOINTS.test(xhr.__dlUrl || '') && xhr.status >= 200 && xhr.status < 300) {
        diffAndPush();
      }
    });
    return originalSend.apply(this, arguments);
  };

  /* -------------------------------------------------------------- view_cart */

  var lastViewedCart = null;

  // Line keys, quantities and total. The drawer can be reopened any number of
  // times without a cart change; only a change should produce a second event.
  function cartSignature(cart) {
    return (cart.items || [])
      .map(function (line) {
        return line.key + ':' + line.quantity;
      })
      .join('|') + '@' + cart.total_price;
  }

  function doViewCart() {
    return readCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return;

      var signature = cartSignature(cart);
      if (signature === lastViewedCart) return;
      lastViewedCart = signature;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency:
            cart.currency ||
            (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
            undefined,
          value: cart.total_price / 100,
          items: cart.items.map(function (line) {
            return mapLine(line);
          })
        }
      });
    });
  }

  // On the same queue as the cart diffs, so adding to cart pushes add_to_cart
  // before the drawer it opens pushes view_cart.
  function viewCart() {
    queue = queue.then(doViewCart).catch(function () {});
    pushListPayloads(document.querySelector('.js-cart-drawer'));
    return queue;
  }

  /* --------------------------- view_item_list for DOM-injected lists */

  var pushedLists = {};

  // Lists whose markup arrives via innerHTML (the drawer upsell) can't ship an
  // executing script, so they ship a JSON payload instead. Fired once per list
  // per page, so reopening the drawer doesn't repeat the impression.
  function pushListPayloads(scope) {
    if (!scope) return;

    scope.querySelectorAll('script[type="application/json"][data-dl-list]').forEach(function (node) {
      var payload;
      try {
        payload = JSON.parse(node.textContent);
      } catch (err) {
        return;
      }

      if (!payload.items || !payload.items.length) return;
      if (pushedLists[payload.item_list_id]) return;
      pushedLists[payload.item_list_id] = true;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency:
            (lastCart && lastCart.currency) ||
            (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
            undefined,
          item_list_id: payload.item_list_id,
          item_list_name: payload.item_list_name,
          items: payload.items
        }
      });
    });
  }

  /* --------------------------------------------------------- begin_checkout */

  function pushBeginCheckout(cart) {
    if (!cart || !cart.items || !cart.items.length) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency:
          cart.currency ||
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          undefined,
        value: cart.total_price / 100,
        items: cart.items.map(function (line) {
          return mapLine(line);
        })
      }
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[name="checkout"], [href="/checkout"], .cart-drawer_checkout');
    if (!btn) return;

    // The browser navigates on this click, so an async /cart.js round trip can
    // lose the event. The cart held in memory is rebuilt on page load and after
    // every intercepted mutation, so it is current — pushing from it is
    // synchronous and lands before the redirect. No delay, no preventDefault.
    if (lastCart) {
      pushBeginCheckout(lastCart);
      return;
    }

    readCart().then(pushBeginCheckout);
  });

  // Queued rather than fired loose, so an add-to-cart click that lands before
  // the first snapshot resolves can't diff against an empty cart and report
  // every existing line as newly added.
  queue = queue.then(readCart).then(takeSnapshot);

  // Exposed for the cart drawer's view_cart push (see spec section 8) so the
  // cart isn't fetched through a second, separate implementation.
  window.dlEcommerce = { readCart: readCart, mapLine: mapLine, viewCart: viewCart };
})();
